import { getConsent, onConsentChange } from './consent';

export interface TrackingConfig {
  ga4_measurement_id: string | null;
  google_ads_conversion_id: string | null;
  google_ads_conversion_label: string | null;
  gtm_container_id: string | null;
  facebook_pixel_id: string | null;
}

export type ScriptTrigger = 'page_load' | 'view' | 'begin_checkout' | 'purchase';

export interface TrackingScript {
  placement: 'head' | 'body_start' | 'body_end';
  trigger?: ScriptTrigger;
  code: string;
}

export interface TrackingVars {
  event_name?: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

let config: TrackingConfig | null = null;
let scripts: TrackingScript[] = [];
let loaded = false;
let currentVars: TrackingVars = {};

const gtag = (...args: unknown[]): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

/**
 * Evento esplicito nel dataLayer: è il gancio per chi usa Google Tag Manager,
 * che può configurare i propri tag sui trigger "event_view" / "begin_checkout" /
 * "purchase" senza fornire script.
 */
const pushDataLayerEvent = (name: string, params: Record<string, unknown>): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
};

/** Sostituisce le variabili {{value}}, {{currency}}, {{transaction_id}}, {{event_name}}. */
const applyVars = (code: string, vars: TrackingVars): string =>
  code
    .replace(/\{\{\s*value\s*\}\}/g, vars.value != null ? String(vars.value) : '')
    .replace(/\{\{\s*currency\s*\}\}/g, vars.currency ?? 'EUR')
    .replace(/\{\{\s*transaction_id\s*\}\}/g, vars.transaction_id ?? '')
    .replace(/\{\{\s*event_name\s*\}\}/g, vars.event_name ?? '');

/** Esegue gli script custom con il trigger indicato (solo a consenso prestato). */
const runTriggerScripts = (trigger: ScriptTrigger, vars: TrackingVars): void => {
  if (!loaded) {
    return;
  }
  scripts
    .filter((s) => (s.trigger ?? 'page_load') === trigger)
    .forEach((s) => injectCustomScript({ ...s, code: applyVars(s.code, vars) }));
};

/**
 * Registra la configurazione di tracciamento dell'evento corrente.
 * Gli script vengono caricati solo dopo il consenso (Consent Mode v2:
 * default "denied" stampato subito, "update granted" all'accettazione).
 */
export const setupTracking = (
  trackingConfig: TrackingConfig | null,
  trackingScripts: TrackingScript[] = []
): void => {
  config = trackingConfig;
  scripts = trackingScripts;

  if (!hasAnyProvider()) {
    return;
  }

  // Consent Mode v2: default negato prima di qualsiasi caricamento tag
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });

  if (getConsent() === 'granted') {
    loadAll();
  } else {
    onConsentChange((status) => {
      if (status === 'granted') {
        loadAll();
      }
    });
  }
};

const hasAnyProvider = (): boolean =>
  !!config &&
  !!(
    config.ga4_measurement_id ||
    config.google_ads_conversion_id ||
    config.gtm_container_id ||
    config.facebook_pixel_id
  ) || scripts.length > 0;

const loadAll = (): void => {
  if (loaded || !hasAnyProvider()) {
    return;
  }
  loaded = true;

  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });

  if (config?.gtm_container_id) {
    loadGtm(config.gtm_container_id);
  }
  if (config?.ga4_measurement_id || config?.google_ads_conversion_id) {
    loadGtag(config.ga4_measurement_id, config.google_ads_conversion_id);
  }
  if (config?.facebook_pixel_id) {
    loadFacebookPixel(config.facebook_pixel_id);
  }
  scripts
    .filter((s) => (s.trigger ?? 'page_load') === 'page_load')
    .forEach((s) => injectCustomScript({ ...s, code: applyVars(s.code, currentVars) }));
};

const loadGtag = (ga4Id: string | null, adsId: string | null): void => {
  const primaryId = ga4Id || adsId;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId as string)}`;
  document.head.appendChild(script);

  window.gtag = gtag;
  gtag('js', new Date());
  if (ga4Id) {
    gtag('config', ga4Id);
  }
  if (adsId) {
    gtag('config', adsId);
  }
};

const loadGtm = (containerId: string): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
};

const loadFacebookPixel = (pixelId: string): void => {
  if (window.fbq) {
    return;
  }
  const fbq: any = (...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  fbq('init', pixelId);
  fbq('track', 'PageView');
};

/**
 * Inserisce uno snippet HTML arbitrario ricreando i nodi <script>
 * (l'innerHTML da solo non li eseguirebbe).
 */
const injectCustomScript = (item: TrackingScript): void => {
  const container = document.createElement('div');
  container.innerHTML = item.code;

  const target =
    item.placement === 'head' ? document.head : document.body;
  const prepend = item.placement === 'body_start';

  Array.from(container.childNodes).forEach((node) => {
    let toInsert: Node = node.cloneNode(true);
    if (node.nodeName === 'SCRIPT') {
      const original = node as HTMLScriptElement;
      const script = document.createElement('script');
      Array.from(original.attributes).forEach((attr) =>
        script.setAttribute(attr.name, attr.value)
      );
      script.text = original.text;
      toInsert = script;
    }
    if (prepend) {
      target.insertBefore(toInsert, target.firstChild);
    } else {
      target.appendChild(toInsert);
    }
  });
};

// ---- Eventi del funnel ----

/** Visita della pagina evento. */
export const trackEventView = (eventName: string): void => {
  currentVars = { ...currentVars, event_name: eventName };
  if (window.gtag) {
    window.gtag('event', 'view_item', { items: [{ item_name: eventName }] });
  }
  if (window.fbq) {
    window.fbq('track', 'ViewContent', { content_name: eventName });
  }
  pushDataLayerEvent('event_view', { event_name: eventName });
  runTriggerScripts('view', { event_name: eventName });
};

/** Inizio iscrizione (submit del form riuscito, prima del pagamento). */
export const trackBeginRegistration = (eventName: string, value?: number): void => {
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'EUR',
      value: value ?? undefined,
      items: [{ item_name: eventName }],
    });
  }
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: eventName,
      currency: 'EUR',
      value: value ?? undefined,
    });
  }
  pushDataLayerEvent('begin_checkout', { event_name: eventName, value, currency: 'EUR' });
  runTriggerScripts('begin_checkout', { event_name: eventName, value, currency: 'EUR' });
};

/** Conversione completata (iscrizione gratuita confermata o pagamento riuscito). */
export const trackConversion = (value: number, transactionId?: string): void => {
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId ?? undefined,
      currency: 'EUR',
      value,
    });
    if (config?.google_ads_conversion_id && config?.google_ads_conversion_label) {
      window.gtag('event', 'conversion', {
        send_to: `${config.google_ads_conversion_id}/${config.google_ads_conversion_label}`,
        currency: 'EUR',
        value,
        transaction_id: transactionId ?? undefined,
      });
    }
  }
  if (window.fbq) {
    window.fbq('track', 'Purchase', { currency: 'EUR', value });
  }
  pushDataLayerEvent('purchase', {
    event_name: currentVars.event_name,
    value,
    currency: 'EUR',
    transaction_id: transactionId,
  });
  runTriggerScripts('purchase', {
    event_name: currentVars.event_name,
    value,
    currency: 'EUR',
    transaction_id: transactionId,
  });
};

// ---- Persistenza per le pagine di esito pagamento (redirect dal backend) ----

const PENDING_KEY = 'pending_conversion';

export interface PendingConversion {
  value: number;
  eventName: string;
  config: TrackingConfig | null;
  scripts: TrackingScript[];
}

/** Da chiamare quando si avvia un pagamento con redirect esterno. */
export const storePendingConversion = (pending: PendingConversion): void => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // senza storage la conversione post-redirect non è tracciabile
  }
};

/** Da chiamare sulla pagina di successo pagamento: traccia e pulisce. */
export const consumePendingConversion = (transactionId?: string): void => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) {
      return;
    }
    sessionStorage.removeItem(PENDING_KEY);
    const pending = JSON.parse(raw) as PendingConversion;
    setupTracking(pending.config, pending.scripts);
    currentVars = { ...currentVars, event_name: pending.eventName };
    // il caricamento tag è sincrono nella coda dataLayer/fbq: l'evento viene accodato
    trackConversion(pending.value, transactionId);
  } catch {
    // dati corrotti o storage non disponibile: nessun tracciamento
  }
};

/** Scarta l'eventuale conversione in attesa (pagamento annullato/fallito). */
export const discardPendingConversion = (): void => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // niente da fare
  }
};
